import React from 'react';
import { Button, Container, InputAdornment, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Lock as LockIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import API, { useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useSnackbar } from '@wheeebo/polaris-snackbar';
import { useErrorHandler } from '../errorHandler';
import { ValidatableField } from '../form';
import { CircleLoader } from '../loader';
const msgs = defineMessages({
    errorInvalidPassword: {
        id: 'pages.guest.PasswordResetPage.error.invalidPassword',
        defaultMessage: 'invalid password',
    },
    headerTitle: {
        id: 'pages.guest.PasswordResetPage.header.title',
        defaultMessage: 'Password Reset',
    },
    buttonBackToLoginPage: {
        id: 'pages.guest.PasswordResetPage.button.backToLoginPage',
        defaultMessage: 'Back to the login page',
    },
    textVerified: {
        id: 'pages.guest.PasswordResetPage.text.verified',
        defaultMessage: 'Your password was successfully reset. Please sign in at the login page',
    },
    textVerificationFailed: {
        id: 'pages.guest.PasswordResetPage.text.verificationFailed',
        defaultMessage: 'The verification failed.',
    },
});
function PasswordResetPage() {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const { register, formState, handleSubmit: validate } = useForm();
    const { errors } = formState;
    const navigate = useNavigate();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { showSuccess } = useSnackbar();
    const { signOut } = useAuth();
    const { csrfToken } = useCsrfToken();
    const [password, setPassword] = React.useState();
    const handleGoBack = async () => {
        await signOut?.();
        navigate('/');
    };
    const { mutate, isError, isSuccess, isLoading } = useMutation((props) => {
        if (!password) {
            return Promise.reject(intl.formatMessage(msgs.errorInvalidPassword));
        }
        return API.Accounts.verifyPasswordReset(csrfToken, props.memberId, props.token, password);
    }, {
        onSuccess: () => {
            showSuccess('Password reset successfully');
        },
        onError: showAndEmitJsErrorEvent,
    });
    const handleSubmit = () => mutate({ memberId: searchParams.get('mid') || '', token: searchParams.get('token') || '' });
    return (React.createElement(Container, { component: "main", sx: { display: 'flex', justifyContent: 'center' } },
        React.createElement("form", { onSubmit: validate(handleSubmit) },
            React.createElement(Paper, { sx: { width: 400, mt: 8, p: 3 } },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Typography, { variant: "h6", align: "center" }, intl.formatMessage(msgs.headerTitle)),
                    isSuccess ? null : (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: "body1" }, "Enter your new password."),
                        React.createElement(ValidatableField, { id: "password", autoComplete: "new-password", label: "Password", fullWidth: true, type: "password", disabled: isLoading, errors, register, registerOptions: {
                                required: true,
                                maxLength: 128,
                                onChange: (e) => setPassword(e.target.value),
                            }, InputProps: {
                                startAdornment: (React.createElement(InputAdornment, { position: "start" },
                                    React.createElement(LockIcon, null))),
                            }, inputProps: { sx: { pl: 1 } } }))),
                    isLoading ? React.createElement(CircleLoader, null) : null,
                    isError ? (React.createElement(Typography, { variant: "body1" }, intl.formatMessage(msgs.textVerificationFailed))) : null,
                    isSuccess ? (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: "body1" }, intl.formatMessage(msgs.textVerified)),
                        React.createElement(Button, { onClick: handleGoBack, startIcon: React.createElement(ArrowBackIcon, null), size: "medium" }, intl.formatMessage(msgs.buttonBackToLoginPage)))) : (React.createElement(Button, { sx: { py: 1 }, disabled: isLoading || !password, type: "submit", size: "medium" }, "Submit")))))));
}
export default React.memo(PasswordResetPage);
